import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { formatAmountInPounds } from "util/formatNumber";

import {
  getConsolidatedWasteAmount,
  WASTE_AMOUNT_TOOLTIP,
  WasteStreamAmounts,
} from "./wasteStreamAmount";

/**
 * Header for the consolidated "Amount" column, with a tooltip explaining how
 * the value is derived. Shared across the tenant and facility waste grids.
 */
export function WasteStreamAmountHeader() {
  return (
    <Tooltip title={WASTE_AMOUNT_TOOLTIP} placement="top">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {/* Match the default MuiDataGrid column header weight so this header
            doesn't read as lighter than the others. */}
        <Box component="span" sx={{ fontWeight: 500 }}>
          Amount
        </Box>
        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
      </Stack>
    </Tooltip>
  );
}

/**
 * Cell renderer for the consolidated "Amount" column. Displays the max across
 * sources. When more than one source reported an amount, a tooltip breaks down
 * the available sources and notes which one is shown.
 */
export function WasteStreamAmountCell({
  vendorAmount,
  eManifestAmount,
  documentAmount,
}: WasteStreamAmounts) {
  const { value, source } = getConsolidatedWasteAmount({
    vendorAmount,
    eManifestAmount,
    documentAmount,
  });
  const display = formatAmountInPounds(value);
  if (!display) return null;

  // Only break down sources that actually reported an amount — a blank source
  // is noise, and the breakdown only adds information when sources disagree.
  const reportedSources = [
    { label: "Vendor", display: formatAmountInPounds(vendorAmount) },
    { label: "e-Manifest", display: formatAmountInPounds(eManifestAmount) },
    { label: "Document", display: formatAmountInPounds(documentAmount) },
  ].filter((s) => s.display);

  if (reportedSources.length < 2) return <span>{display}</span>;

  const tooltip = (
    <Stack spacing={0.25}>
      {reportedSources.map((s) => (
        <Typography key={s.label} variant="caption">
          {s.label}: {s.display}
        </Typography>
      ))}
      <Typography variant="caption" sx={{ fontStyle: "italic" }}>
        Showing the maximum reported amount ({source}).
      </Typography>
    </Stack>
  );

  return (
    <Tooltip title={tooltip}>
      <span>{display}</span>
    </Tooltip>
  );
}
